import React, { FC, useEffect, useId, useRef } from 'react';
import classNames from 'classnames';

import { translate } from '@/translation/translate.ts';
import { ICONS } from '@/Icons.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig';
import { useLayer } from '@/layers/useLayer.tsx';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';

interface LayerDisplayProps {
  templateData: any;
}

export const LayerDisplay: FC<LayerDisplayProps> = ({ templateData }) => {
  const { layerState, closeLayer, content } = useLayer();
  const { eventConfig } = useEventConfig();
  const { handleUiEvent } = useUiEvent();
  const titleId = useId();
  const descId = useId();

  const layerStyle = {
    Layer: true,
    closing: layerState === 'closing',
    closed: layerState === 'closed',
    visible: layerState === 'visible',
  };

  // Ref to the layer content for focus management
  const layerContentRef = useRef<HTMLDivElement | null>(null);

  // Returns focusable elements within the container
  const getFocusableElements = (root: HTMLElement): HTMLElement[] => {
    const selector = [
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
    return nodes.filter((el) => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && !el.hasAttribute('disabled') && el.tabIndex !== -1;
    });
  };

  const closeLabel = translate(eventConfig?.translations.close);

  // Remember previous focus and trap/focus inside the layer while visible
  const lastFocusedBeforeLayerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (layerState === 'visible') {
      const active = document.activeElement as HTMLElement | null;
      lastFocusedBeforeLayerRef.current = active && typeof active.focus === 'function' ? active : null;

      // Move focus into the layer
      setTimeout(() => {
        const container = layerContentRef.current;
        if (container) {
          // Prefer first button in .layer-buttons; fallback to .layer-close; then generic first focusable; then container
          const preferredFirst =
            container.querySelector<HTMLElement>('.layer-buttons button') ??
            container.querySelector<HTMLElement>('button.layer-close');

          if (preferredFirst && typeof preferredFirst.focus === 'function') {
            preferredFirst.focus();
            return;
          }

          const focusables = getFocusableElements(container);
          const el = focusables[0] ?? container;
          if (typeof el.focus === 'function') {
            el.focus();
          }
        }
      }, 0);
    }
    if (layerState === 'closed') {
      const el = lastFocusedBeforeLayerRef.current;
      if (el && typeof el.focus === 'function') {
        setTimeout(() => {
          el.focus();
        }, 0);
      }
      lastFocusedBeforeLayerRef.current = null;
    }
  }, [layerState]);

  // Arrow key roving focus between layer-close and action buttons; selection remains via click/Enter/Space
  // Also trap focus with Tab/Shift+Tab when the layer is visible
  const handleRovingKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    const key = e.key;

    // Close on Escape
    if (key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeLayer();
      return;
    }

    // Focus trap: keep Tab navigation within the layer when visible
    if (layerState === 'visible' && key === 'Tab') {
      const container = layerContentRef.current ?? e.currentTarget;
      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // If focus is outside the container, push it inside on next Tab press
      const activeInside = active && container.contains(active);

      if (e.shiftKey) {
        if (!activeInside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!activeInside || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
      return;
    }

    // Roving with arrow keys only within the action buttons/close button cluster
    if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'ArrowDown' && key !== 'ArrowUp') {
      return;
    }
    const container = e.currentTarget;
    const nodeList = container.querySelectorAll<HTMLButtonElement>('button.layer-close, .layer-buttons button');
    const buttons = Array.from(nodeList) as HTMLElement[];
    if (buttons.length === 0) return;

    const active = document.activeElement as HTMLElement | null;
    const idx = active ? buttons.indexOf(active) : -1;

    e.preventDefault();
    if (idx === -1) {
      buttons[0].focus();
      return;
    }

    const dir = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
    const nextIdx = (idx + dir + buttons.length) % buttons.length;
    buttons[nextIdx].focus();
  };

  return (
    <div className={classNames(layerStyle)}>
      {content && (
        <>
          <div className="layer-mask" onClick={closeLayer} aria-hidden="true"></div>

          <div
            className="layer-content"
            ref={layerContentRef}
            tabIndex={-1}
            onKeyDown={handleRovingKeyDown}
            role="dialog"
            aria-modal={true}
            aria-labelledby={titleId}
            aria-describedby={descId}
          >
            <h1 id={titleId}>
              {content.icon && <i className={ICONS[content.icon]} aria-hidden="true" />}
              {translate(content.title)}
            </h1>
            <div className="layer-text" id={descId}>
              {fromTemplate(content.text, templateData)}
            </div>
            {content.closeable && (
              <button className="layer-close" onClick={closeLayer}>
                {closeLabel}
              </button>
            )}
            <div className="layer-buttons">
              {content.buttons &&
                content.buttons.map((config, i) => {
                  const key = config.label.en || i;
                  return (
                    <button key={key} onClick={() => handleUiEvent(config.click)}>
                      <span className="loader" aria-hidden="true"></span>
                      <i className={ICONS[config.icon]} aria-hidden="true" />
                      {translate(config.label)}
                    </button>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
